/**
 * Branded id primitives.
 *
 * Design 4.1 S3: "Ids are UUID v7, generated in the application
 * (`packages/domain/src/id.ts`), so primary keys are time-ordered and index bloat stays
 * low. Branded TypeScript types (`AccountId`, `WorkspaceId`, `ConversationId`, ...) cannot
 * be built from a bare string or interchanged."
 *
 * `Branded<"AccountId">` expands to exactly the shape design 12.2 writes out by hand:
 * `string & { readonly __brand: "AccountId" }`.
 */
const UUID_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
/** The nil UUID, which design 4.1 S2 CHECK-constrains every tenant id against. */
export const NIL_UUID = "00000000-0000-0000-0000-000000000000";
/** Thrown when a string is not a well-formed, non-nil UUID for the named brand. */
export class MalformedIdError extends Error {
    name = "MalformedIdError";
    brandName;
    value;
    constructor(brandName, value) {
        super(`${brandName} must be a non-nil lowercase UUID; received ${JSON.stringify(value)}`);
        this.brandName = brandName;
        this.value = value;
    }
}
const HEX = "0123456789abcdef";
function toHex(bytes, from, to) {
    let out = "";
    for (let i = from; i < to; i += 1) {
        const b = bytes[i] ?? 0;
        out += HEX[(b >> 4) & 0x0f];
        out += HEX[b & 0x0f];
    }
    return out;
}
/**
 * RFC 9562 UUID version 7: 48-bit big-endian Unix millisecond timestamp, version and
 * variant bits, 74 bits of randomness. Time-ordered, which is the property design 4.1 S3
 * asks for.
 *
 * Both inputs are required. There is no ambient clock and no ambient randomness.
 */
export function uuidV7(clock, random) {
    const bytes = random.fill(new Uint8Array(16));
    const ms = Math.trunc(clock.now());
    if (!Number.isFinite(ms) || ms < 0) {
        throw new RangeError(`Clock returned an unusable epoch milliseconds value: ${String(ms)}`);
    }
    bytes[0] = (ms / 2 ** 40) & 0xff;
    bytes[1] = (ms / 2 ** 32) & 0xff;
    bytes[2] = (ms / 2 ** 24) & 0xff;
    bytes[3] = (ms / 2 ** 16) & 0xff;
    bytes[4] = (ms / 2 ** 8) & 0xff;
    bytes[5] = ms & 0xff;
    bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x70; // version 7
    bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80; // variant 10
    return (`${toHex(bytes, 0, 4)}-${toHex(bytes, 4, 6)}-${toHex(bytes, 6, 8)}-` +
        `${toHex(bytes, 8, 10)}-${toHex(bytes, 10, 16)}`);
}
/** Reads the embedded millisecond timestamp out of a UUID v7. */
export function uuidV7TimestampMs(value) {
    if (!UUID_SHAPE.test(value))
        throw new MalformedIdError("uuid", value);
    return Number.parseInt(value.slice(0, 8) + value.slice(9, 13), 16);
}
/** Builds the codec for one branded id type. */
export function idCodec(brandName) {
    const codec = {
        brandName,
        is(value) {
            return UUID_SHAPE.test(value) && value !== NIL_UUID;
        },
        parse(value) {
            if (!codec.is(value))
                throw new MalformedIdError(brandName, value);
            return value;
        },
        next(clock, random) {
            return uuidV7(clock, random);
        },
    };
    return Object.freeze(codec);
}
//# sourceMappingURL=brand.js.map